import React, { useState, useEffect } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import {
  InfoCard,
  Header,
  Page,
  Content,
  HeaderLabel,
  MarkdownContent,
  Progress,
} from '@backstage/core-components';
import welcomePath from './01-welcome.md';
import welcomeGuestPath from './01-welcome-guest.md';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import { useGuestState } from '../guest';

const useStyles = makeStyles(theme => ({
  buttonLinks: {
    '& p > a': {
      display: 'inline-block',
      padding: theme.spacing(0.75, 2),
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: theme.typography.button.fontWeight,
      textTransform: theme.typography.button.textTransform,
      textDecoration: 'none',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        textDecoration: 'none',
      },
    },
  },
}));

const MarkdownCard = ({
  filePath,
  title,
  className,
}: {
  filePath: string;
  title: string;
  className?: string;
}) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setContent('');
    setLoading(true);

    fetch(filePath)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load ${filePath}: ${response.status}`);
        }
        return response.text();
      })
      .then(text => {
        if (!mounted) {
          return;
        }
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setContent('');
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [filePath]);

  return (
    <InfoCard title={title}>
      {loading ? (
        <Progress />
      ) : (
        <MarkdownContent content={content} className={className} />
      )}
    </InfoCard>
  );
};

type HomeUser = {
  displayName: string;
  group: string;
};

const firstGroupName = (ownershipEntityRefs: string[]) => {
  const groupRef = ownershipEntityRefs
    .map(ref => parseEntityRef(ref))
    .find(ref => ref.kind.toLowerCase() === 'group');
  return groupRef?.name ?? 'N/A';
};

export const HomePage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const { isGuest } = useGuestState();
  const [user, setUser] = useState<HomeUser>();

  useEffect(() => {
    if (isGuest) {
      return undefined;
    }

    let mounted = true;

    Promise.all([
      identityApi.getProfileInfo(),
      identityApi.getBackstageIdentity(),
    ])
      .then(([profile, identity]) => {
        if (mounted) {
          setUser({
            displayName: profile.displayName ?? identity.userEntityRef,
            group: firstGroupName(identity.ownershipEntityRefs),
          });
        }
      })
      .catch(() => {
        if (mounted) {
          setUser(undefined);
        }
      });

    return () => {
      mounted = false;
    };
  }, [identityApi, isGuest]);

  return (
    <Page themeId="home">
      <Header title="Allfunds DevPortal" subtitle="Your entry point to development resources">
        {!isGuest && user && (
          <>
            <HeaderLabel label="User" value={user.displayName} />
            <HeaderLabel label="Group" value={user.group} />
          </>
        )}
      </Header>
      <Content>
        <Grid container spacing={3} direction="column">
          <Grid item>
            <MarkdownCard
              key={isGuest ? 'guest' : 'authenticated'}
              filePath={isGuest ? welcomeGuestPath : welcomePath}
              title="Welcome to the Allfunds Developer Portal!"
              className={isGuest ? classes.buttonLinks : undefined}
            />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
